/**
 * Composición de la consola: reloj, sesión, caché de consultas, cliente de la API con la sesión y las
 * rutas. Lo único que cambia entre la demostración y la real es qué identidad y qué `fetch` se usan.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router";
import { ProveedorSesion, RequiereSesion, useSesion, type ProveedorIdentidad } from "@/features/sesion";
import {
  Auditoria,
  Ficha,
  Ingresar,
  Instituciones,
  Invitar,
  NoEncontrada,
  Retorno,
  SinPermiso,
  Usuarios,
} from "@/pages";
import { crearClienteApi, ErrorApi, ProveedorApi } from "@/shared/api";
import { ProveedorReloj } from "@/shared/lib/reloj";
import { PlantillaConsola } from "@/templates";

export interface Configuracion {
  readonly demo: boolean;
  readonly apiUrl: string;
  readonly identidad: ProveedorIdentidad;
  /** `fetch` de la API; en la demostración, el servidor falso. */
  readonly fetch?: typeof globalThis.fetch;
}

/** Un 4xx no se reintenta (no va a cambiar); un error de red o 5xx, hasta dos veces. */
export function reintentar(intentos: number, error: unknown): boolean {
  if (error instanceof ErrorApi && error.estado >= 400 && error.estado < 500) return false;
  return intentos < 2;
}

/** El cliente de la API usa el token de la sesión; un 401 termina la sesión. Al salir, se vacía la caché. */
function ApiConSesion({
  config,
  cache,
  children,
}: {
  readonly config: Configuracion;
  readonly cache: QueryClient;
  readonly children: ReactNode;
}) {
  const { obtenerToken, perder, empleado } = useSesion();
  const cliente = useMemo(
    () =>
      crearClienteApi({
        baseUrl: config.apiUrl,
        obtenerToken,
        alPerderSesion: perder,
        ...(config.fetch ? { fetch: config.fetch } : {}),
      }),
    [config.apiUrl, config.fetch, obtenerToken, perder],
  );
  useEffect(() => {
    if (!empleado) cache.clear();
  }, [empleado, cache]);
  return <ProveedorApi cliente={cliente}>{children}</ProveedorApi>;
}

export function crearRutas(demo: boolean) {
  return createBrowserRouter([
    { path: "/ingreso", element: <Retorno demo={demo} /> },
    {
      path: "/",
      element: (
        <RequiereSesion sinSesion={<Ingresar demo={demo} />} sinPermiso={<SinPermiso demo={demo} />}>
          <PlantillaConsola demo={demo}>
            <Outlet />
          </PlantillaConsola>
        </RequiereSesion>
      ),
      children: [
        { index: true, element: <Usuarios /> },
        { path: "usuarios/nuevo", element: <Invitar /> },
        { path: "usuarios/:usuarioId", element: <Ficha /> },
        { path: "instituciones", element: <Instituciones /> },
        { path: "auditoria", element: <Auditoria /> },
        { path: "*", element: <NoEncontrada /> },
      ],
    },
  ]);
}

export function Aplicacion({ config }: { readonly config: Configuracion }) {
  const [cache] = useState(
    () => new QueryClient({ defaultOptions: { queries: { retry: reintentar, refetchOnWindowFocus: true } } }),
  );
  const [rutas] = useState(() => crearRutas(config.demo));
  return (
    <ProveedorReloj ahora={() => new Date()}>
      <ProveedorSesion identidad={config.identidad}>
        <QueryClientProvider client={cache}>
          <ApiConSesion config={config} cache={cache}>
            <RouterProvider router={rutas} />
          </ApiConSesion>
        </QueryClientProvider>
      </ProveedorSesion>
    </ProveedorReloj>
  );
}
